import React, { useState } from "react";
import Banner from "../../../Components/Common/Banner";
import GetInTouchUs from "../../../Components/Common/GetInTouchUs";
import partner_withUs from "../../../Assets/images/partner_withUs.png";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import emailjs from "@emailjs/browser";

const PartnerWithUs = () => {
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    companyWebsite: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const handleClear = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      companyName: "",
      companyWebsite: "",
      message: "",
    });

    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Remove error as soon as user starts correcting the field
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name
    const name = formData.name.trim();

    if (!name) {
      newErrors.name = "Name is required.";
    } else if (name.length < 2) {
      newErrors.name = "Name must be at least 2 characters.";
    } else if (!/^[A-Za-z\s.'-]+$/.test(name)) {
      newErrors.name = "Please enter a valid name.";
    }

    // Email
    const email = formData.email.trim();

    if (!email) {
      newErrors.email = "Email is required.";
    } else if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)
    ) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Phone
    const phone = formData.phone.trim();

    if (!phone) {
      newErrors.phone = "Phone number is required.";
    } else if (!/^[6-9]\d{9}$/.test(phone.replace(/\s+/g, ""))) {
      newErrors.phone = "Please enter a valid 10-digit phone number.";
    }

    // Company Name
    const companyName = formData.companyName.trim();

    if (!companyName) {
      newErrors.companyName = "Company name is required.";
    } else if (companyName.length < 2) {
      newErrors.companyName =
        "Company name must be at least 2 characters.";
    }

    // Company Website
    const companyWebsite = formData.companyWebsite.trim();

    if (companyWebsite) {
      try {
        const url = new URL(companyWebsite);

        if (!["http:", "https:"].includes(url.protocol)) {
          newErrors.companyWebsite =
            "Please enter a valid website URL.";
        }
      } catch {
        newErrors.companyWebsite =
          "Please enter a valid website URL.";
      }
    }

    // Message
    const message = formData.message.trim();

    if (!message) {
      newErrors.message = "Message is required.";
    } else if (message.length < 10) {
      newErrors.message =
        "Message must be at least 10 characters.";
    } else if (message.length > 1000) {
      newErrors.message =
        "Message cannot exceed 1000 characters.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate before sending request
    const isValid = validateForm();

    if (!isValid) {
      toast.error("Please fix the errors in the form.");
      return;
    }

    setIsLoading(true);

    try {
      const emailResponse = await emailjs.send(
        "service_lw92dka",
        "template_d23bf2h",
        {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          companyName: formData.companyName.trim(),
          companyWebsite: formData.companyWebsite.trim(),
          message: formData.message.trim(),
        },
        "Ic_RaxZmAKBkf8h5i"
      );

      console.log("Email Success:", emailResponse);

      toast.success("Successfully submitted request!");

      handleClear();
    } catch (error) {
      console.error("EmailJS Error:", error);

      toast.error(
        "Something went wrong while submitting your request. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <ToastContainer />

      <Banner
        pageName="PARTNER WITH US"
        visible={false}
        breadcrummbs="Home / Contact Us / Partner With Us"
        image={partner_withUs}
      />

      <div className="lg:px-20 md:px-10 px-5 py-10">
        <div>
          <h1 className="text-gray-600 font-medium md:text-xl border-b-2 border-primaryDarkColor inline-block">
            BECOME A PARTNER !
          </h1>

          <p className="text-secondaryTextColor tracking-wide text-justify mt-4">
            Are you interested in integrating sustainable actions at the core
            of your business? It can be anything starting from training to
            software development. You can be a part of us to make it bigger.
            We assure you that the benefits are many and we will be more than
            happy to guide you through.
          </p>

          <p className="text-secondaryTextColor tracking-wide text-justify mt-2">
            Please fill the information below and we will get in touch with
            you. You can also directly send a message to
            partner@techmenteetechgroup.com. See you soon.
          </p>
        </div>

        <div className="xl:grid xl:grid-cols-2 flex flex-col-reverse gap-4 mt-8 mb-10">
          {/* Image */}
          <div>
            <img
              src="https://images.unsplash.com/photo-1576267423429-569309b31e84?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Partner with us"
              className="w-full rounded-xl h-full object-cover"
            />
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="lg:p-10 md:p-6 p-4 rounded-lg bg-primaryOrangeColor"
            noValidate
          >
            <div className="grid grid-cols-1 gap-3">

              {/* Name */}
              <div>
                <input
                  type="text"
                  placeholder="Name"
                  onChange={handleChange}
                  name="name"
                  value={formData.name}
                  className={`w-full border rounded px-4 py-2 outline-none ${
                    errors.name
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                {errors.name && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  onChange={handleChange}
                  name="email"
                  value={formData.email}
                  className={`w-full border rounded px-4 py-2 outline-none ${
                    errors.email
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <input
                  type="tel"
                  placeholder="Phone"
                  onChange={handleChange}
                  name="phone"
                  value={formData.phone}
                  maxLength={10}
                  className={`w-full border rounded px-4 py-2 outline-none ${
                    errors.phone
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                {errors.phone && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Company Name */}
              <div>
                <input
                  type="text"
                  placeholder="Enter Company Name"
                  onChange={handleChange}
                  name="companyName"
                  value={formData.companyName}
                  className={`w-full border rounded px-4 py-2 outline-none ${
                    errors.companyName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                {errors.companyName && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.companyName}
                  </p>
                )}
              </div>

              {/* Company Website */}
              <div>
                <input
                  type="url"
                  placeholder="Enter Company Website"
                  onChange={handleChange}
                  name="companyWebsite"
                  value={formData.companyWebsite}
                  className={`w-full border rounded px-4 py-2 outline-none ${
                    errors.companyWebsite
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                {errors.companyWebsite && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.companyWebsite}
                  </p>
                )}
              </div>

              {/* Message */}
              <div>
                <textarea
                  placeholder="Enter Message"
                  onChange={handleChange}
                  name="message"
                  value={formData.message}
                  rows={5}
                  maxLength={1000}
                  className={`w-full border rounded px-4 py-2 outline-none resize-none ${
                    errors.message
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                <div className="flex justify-between">
                  {errors.message ? (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.message}
                    </p>
                  ) : (
                    <span />
                  )}

                  <span className="text-gray-500 text-xs mt-1">
                    {formData.message.length}/1000
                  </span>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="mt-4 flex items-center justify-center">
              <button
                type="submit"
                disabled={isLoading}
                className="relative inline-flex cursor-pointer disabled:cursor-not-allowed disabled:opacity-70 items-center justify-start px-20 py-2 overflow-hidden font-medium transition-all bg-white rounded hover:bg-white group"
              >
                <span className="w-60 h-48 rounded rotate-[-40deg] bg-gradient-to-l from-purple-800 to-purple-500 absolute bottom-0 left-0 -translate-x-full ease-out duration-500 transition-all translate-y-full mb-9 ml-9 group-hover:ml-0 group-hover:mb-32 group-hover:translate-x-0" />

                <span className="relative w-full flex items-center justify-center gap-2 text-left text-black transition-colors duration-300 ease-in-out group-hover:text-white">
                  {isLoading ? (
                    <>
                      <div className="rounded-full h-5 w-5 border-2 border-gray-400 border-t-transparent animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Submit"
                  )}
                </span>
              </button>
            </div>
          </form>
        </div>

        <GetInTouchUs />
      </div>
    </div>
  );
};

export default PartnerWithUs;